import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import {
  BarChart2,
  Send,
  Inbox,
  Sparkles,
  TrendingUp,
  Calendar,
  ArrowRight,
  Lightbulb,
  Bookmark,
  X,
  Sun,
  ChevronRight,
  Layers,
  PenLine,
} from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import { TopNav } from '@/components/layout/TopNav';
import { ForYouFeed } from '@/components/dashboard/ForYouFeed';
import { FirstRunSetup } from '@/components/dashboard/FirstRunSetup';
import { Card, CardHeader, CardLink } from '@/components/ui/Card';
import { Button, IconButton } from '@/components/ui/Button';
import { Badge, postStatusTone } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { useAppStore } from '@/stores/appStore';
import {
  postsApi,
  inboxApi,
  trendsApi,
  briefApi,
  type TrendRecommendation,
  type BriefItem,
} from '@/lib/api';
import { formatRelativeTime, parseDbDate } from '@/lib/utils';
import { PLATFORMS } from '@/lib/platforms';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

function formatDay(value: string | null | undefined, pattern: string): string | null {
  if (!value) return null;
  const d = value.length <= 10 ? parseISO(value) : parseDbDate(value);
  return isValid(d) ? format(d, pattern) : null;
}

const BRIEF_ACTION_LABEL: Partial<Record<BriefItem['type'], string>> = {
  recycle_winner: 'Recycle',
  trend_idea: 'View idea',
  schedule_gap: 'Fill the queue',
  inbox_backlog: 'Open inbox',
  publish_failures: 'Review',
};

function briefTone(type: BriefItem['type']) {
  if (type === 'publish_failures') return { dot: 'bg-destructive', label: 'Needs attention' };
  if (type === 'schedule_gap' || type === 'inbox_backlog') return { dot: 'bg-warning', label: 'Heads up' };
  if (type === 'all_clear') return { dot: 'bg-success', label: 'All clear' };
  return { dot: 'bg-accent', label: 'Opportunity' };
}

export function Dashboard() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['posts', currentWorkspace?.id, 'recent'],
    queryFn: async () => {
      const token = await getToken();
      return postsApi.list(token!, { workspaceId: currentWorkspace!.id, limit: 5 });
    },
    enabled: !!currentWorkspace,
  });

  const { data: scheduledData, isLoading: scheduledLoading } = useQuery({
    queryKey: ['posts', currentWorkspace?.id, 'upcoming'],
    queryFn: async () => {
      const token = await getToken();
      // "Upcoming": the next posts due to go out, soonest first.
      return postsApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        status: 'scheduled',
        from: new Date().toISOString(),
        sort: 'scheduled_asc',
        limit: 5,
      });
    },
    enabled: !!currentWorkspace,
  });

  const { data: inboxCount } = useQuery({
    queryKey: ['inbox-count', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return inboxApi.unreadCount(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  // Real workspace-wide counts — the recent/scheduled lists above are capped
  // at 5 rows and must never feed the stat tiles.
  const { data: postStats, isLoading: statsLoading } = useQuery({
    queryKey: ['posts', currentWorkspace?.id, 'stats'],
    queryFn: async () => {
      const token = await getToken();
      return postsApi.stats(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  const stats = [
    {
      label: 'Scheduled',
      value: postStats?.byStatus['scheduled'] ?? 0,
      icon: Calendar,
      to: '/calendar',
    },
    {
      label: 'Published',
      value: postStats?.byStatus['published'] ?? 0,
      icon: Send,
      to: '/publishing',
    },
    {
      label: 'Unread inbox',
      value: inboxCount?.count ?? 0,
      icon: Inbox,
      to: '/inbox',
    },
    {
      label: 'Total posts',
      value: postStats?.total ?? 0,
      icon: BarChart2,
      to: '/analytics',
    },
  ];

  // Morning Brief: the daily rule-based synthesis of everything the platform
  // pulled overnight, each item with a one-click action.
  const { data: briefData } = useQuery({
    queryKey: ['morning-brief', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return briefApi.get(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const briefItems = briefData?.brief.items ?? [];

  const recycleWinner = useMutation({
    mutationFn: async (postId: string) => {
      const token = await getToken();
      return postsApi.duplicate(token!, postId);
    },
    onSuccess: () => navigate('/publishing'),
  });

  const briefAction = (item: BriefItem) => {
    if (item.type === 'recycle_winner' && item.postId) {
      recycleWinner.mutate(item.postId);
      return;
    }
    navigate(item.link);
  };

  // Trend-driven content ideas — surfaced where the team starts their day
  // instead of buried in Settings.
  const { data: ideasData } = useQuery({
    queryKey: ['trend-recommendations', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return trendsApi.recommendations(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });
  const ideas = (ideasData?.recommendations ?? [])
    .filter((r) => r.status === 'new' || r.status === 'saved')
    .slice(0, 3);

  const setIdeaStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TrendRecommendation['status'] }) => {
      const token = await getToken();
      return trendsApi.setRecommendationStatus(token!, id, status);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['trend-recommendations', currentWorkspace?.id] }),
  });

  const useIdea = (idea: TrendRecommendation) => {
    setIdeaStatus.mutate({ id: idea.id, status: 'used' });
    let hashtags: string[] = [];
    try {
      hashtags = idea.suggested_hashtags ? (JSON.parse(idea.suggested_hashtags) as string[]) : [];
    } catch {
      /* hashtags stay empty on malformed data */
    }
    const prefillContent = `${idea.idea}${hashtags.length ? `\n\n${hashtags.join(' ')}` : ''}`;
    navigate('/publishing?compose=true', { state: { prefillContent } });
  };

  const recentPosts = postsData?.posts ?? [];
  const upcomingPosts = scheduledData?.posts ?? [];
  const compose = () => navigate('/publishing?compose=true');

  if (!currentWorkspace) {
    return (
      <div className="flex-1 overflow-auto">
        <TopNav title="Dashboard" />
        <div className="flex min-h-[calc(100dvh-4rem)] items-center justify-center p-6">
          <div className="sfx-fade max-w-sm text-center">
            <span className="sfx-icon-tile mx-auto mb-5 h-12 w-12">
              <Layers className="h-5 w-5" />
            </span>
            <h2 className="font-display text-2xl font-semibold tracking-[-0.02em] text-foreground">
              Welcome to Enflux
            </h2>
            <FirstRunSetup />
          </div>
        </div>
      </div>
    );
  }

  const briefDate = formatDay(briefData?.brief.brief_date, 'EEE, MMM d');

  return (
    <div className="flex-1 overflow-auto">
      <TopNav
        title="Dashboard"
        description="What to post next, and what needs you today"
        action={{ label: 'New Post', onClick: compose }}
      />

      <div className="mx-auto max-w-[1400px] space-y-6 p-6 lg:p-8">
        {/* Stats: one strip, four cells, each a door into its section */}
        <Card className="grid grid-cols-2 lg:grid-cols-4" data-tour="dashboard-stats">
          {stats.map((stat, i) => (
            <button
              key={stat.label}
              type="button"
              onClick={() => navigate(stat.to)}
              className={cn(
                'group flex flex-col gap-4 p-5 text-left transition-colors duration-fast ease-sfx hover:bg-tertiary/30',
                i % 2 === 1 && 'border-l border-hairline',
                i >= 2 && 'border-t border-hairline lg:border-t-0',
                i === 2 && 'lg:border-l'
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  {stat.label}
                </span>
                <span className="sfx-icon-tile h-7 w-7">
                  <stat.icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                </span>
              </div>
              {statsLoading && stat.to !== '/inbox' ? (
                <span className="sfx-skeleton h-8 w-14" />
              ) : (
                <span className="font-display text-[32px] font-semibold leading-none tracking-[-0.03em] text-foreground tabular-nums">
                  {stat.value.toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </Card>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          {/* ── Left: what to do ── */}
          <div className="min-w-0 space-y-6">
            {briefItems.length > 0 && (
              <Card featured data-tour="morning-brief">
                <CardHeader
                  icon={Sun}
                  title="Morning Brief"
                  description="What Enflux noticed overnight, each with a next step"
                  action={
                    briefDate && (
                      <span className="text-[11px] text-muted-foreground tabular-nums">{briefDate}</span>
                    )
                  }
                />
                <ul className="divide-y divide-hairline border-t border-hairline">
                  {briefItems.map((item, i) => {
                    const tone = briefTone(item.type);
                    return (
                      <li key={i} className="flex items-start gap-3 px-5 py-4">
                        <span
                          className={cn('mt-[7px] h-2 w-2 shrink-0 rounded-full', tone.dot)}
                          role="img"
                          aria-label={tone.label}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">{item.title}</p>
                          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                            {item.body}
                          </p>
                        </div>
                        {item.type !== 'all_clear' && (
                          <Button
                            variant="accent-soft"
                            onClick={() => briefAction(item)}
                            loading={item.type === 'recycle_winner' && recycleWinner.isPending}
                            disabled={recycleWinner.isPending}
                          >
                            {BRIEF_ACTION_LABEL[item.type] ?? 'Open'}
                            <ArrowRight className="h-3 w-3" />
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}

            {/* For You — the recommendations engine feed */}
            <ForYouFeed />

            {/* Content ideas from trend intelligence */}
            {ideas.length > 0 && (
              <Card>
                <CardHeader
                  icon={Lightbulb}
                  title="Content ideas"
                  description="Drafted from your brand kit, what has performed, and this week's trends"
                  action={
                    <CardLink onClick={() => navigate('/settings/trends')}>
                      All ideas
                      <ArrowRight className="h-3 w-3 transition-transform duration-fast ease-sfx group-hover:translate-x-0.5" />
                    </CardLink>
                  }
                />
                <div className="grid grid-cols-1 divide-y divide-hairline border-t border-hairline md:grid-cols-3 md:divide-x md:divide-y-0">
                  {ideas.map((idea) => (
                    <div key={idea.id} className="flex flex-col gap-2 p-5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold leading-snug text-foreground">{idea.title}</p>
                        {idea.platform && (
                          <Badge>
                            <PlatformIcon platform={idea.platform} size="xs" variant="glyph" />
                            {PLATFORMS[idea.platform as keyof typeof PLATFORMS]?.label ?? idea.platform}
                          </Badge>
                        )}
                      </div>
                      <p className="line-clamp-3 flex-1 text-xs leading-relaxed text-muted-foreground">
                        {idea.idea}
                      </p>
                      <div className="flex items-center gap-1 pt-2">
                        <Button variant="accent-soft" onClick={() => useIdea(idea)}>
                          <PenLine className="h-3 w-3" />
                          Use idea
                        </Button>
                        {idea.status !== 'saved' && (
                          <IconButton
                            label="Save for later"
                            onClick={() => setIdeaStatus.mutate({ id: idea.id, status: 'saved' })}
                          >
                            <Bookmark className="h-3.5 w-3.5" />
                          </IconButton>
                        )}
                        <IconButton
                          label="Dismiss"
                          tone="danger"
                          onClick={() => setIdeaStatus.mutate({ id: idea.id, status: 'dismissed' })}
                        >
                          <X className="h-3.5 w-3.5" />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* ── Right: what's happening ── */}
          <div className="min-w-0 space-y-6">
            <Card>
              <CardHeader
                title="Upcoming"
                description="Next posts in the publishing queue"
                action={
                  <CardLink onClick={() => navigate('/calendar')}>
                    Calendar
                    <ArrowRight className="h-3 w-3 transition-transform duration-fast ease-sfx group-hover:translate-x-0.5" />
                  </CardLink>
                }
              />
              <div className="border-t border-hairline">
                {scheduledLoading ? (
                  <ListSkeleton rows={3} />
                ) : upcomingPosts.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Calendar}
                    title="Nothing scheduled"
                    body="Keep the cadence alive: schedule a post or fill a queue."
                    action={
                      <Button variant="secondary" onClick={compose}>
                        Schedule a post
                      </Button>
                    }
                  />
                ) : (
                  <ul className="divide-y divide-hairline">
                    {upcomingPosts.map((post) => (
                      <li key={post.id} className="flex items-center gap-3 px-5 py-3">
                        <div className="flex w-11 shrink-0 flex-col items-center rounded-m bg-tertiary/60 py-1.5 ring-1 ring-inset ring-hairline">
                          <span className="text-[10px] font-medium uppercase leading-none text-muted-foreground">
                            {formatDay(post.scheduled_at, 'MMM') ?? ''}
                          </span>
                          <span className="mt-0.5 font-display text-base font-semibold leading-none text-foreground tabular-nums">
                            {formatDay(post.scheduled_at, 'd') ?? ''}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="line-clamp-1 text-sm text-foreground">{post.content}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
                            {formatDay(post.scheduled_at, 'EEE, h:mm a')}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Recent posts"
                description="Latest activity across your channels"
                action={
                  <CardLink onClick={() => navigate('/publishing')}>
                    View all
                    <ArrowRight className="h-3 w-3 transition-transform duration-fast ease-sfx group-hover:translate-x-0.5" />
                  </CardLink>
                }
              />
              <div className="border-t border-hairline">
                {postsLoading ? (
                  <ListSkeleton rows={3} />
                ) : recentPosts.length === 0 ? (
                  <EmptyState
                    compact
                    icon={PenLine}
                    title="No posts yet"
                    body="Draft your first post, or let AI Studio write one in your brand voice."
                    action={
                      <Button variant="secondary" onClick={() => navigate('/ai-studio')}>
                        <Sparkles className="h-3.5 w-3.5" />
                        Open AI Studio
                      </Button>
                    }
                  />
                ) : (
                  <ul className="divide-y divide-hairline">
                    {recentPosts.map((post) => (
                      <li key={post.id} className="px-5 py-3">
                        <p className="line-clamp-1 text-sm text-foreground">{post.content}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <Badge tone={postStatusTone(post.status)} className="capitalize">
                            {post.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(post.created_at)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            <Card>
              <ul className="divide-y divide-hairline">
                {[
                  {
                    icon: Send,
                    label: 'Create post',
                    desc: 'Schedule or publish now',
                    onClick: compose,
                  },
                  {
                    icon: Sparkles,
                    label: 'AI Studio',
                    desc: 'Generate on-brand copy and images',
                    onClick: () => navigate('/ai-studio'),
                  },
                  {
                    icon: TrendingUp,
                    label: 'Analytics',
                    desc: 'See what is working',
                    onClick: () => navigate('/analytics'),
                  },
                ].map((action) => (
                  <li key={action.label}>
                    <button
                      type="button"
                      onClick={action.onClick}
                      className="group flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors duration-fast ease-sfx hover:bg-tertiary/30"
                    >
                      <span className="sfx-icon-tile h-8 w-8">
                        <action.icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-foreground">{action.label}</span>
                        <span className="block text-xs text-muted-foreground">{action.desc}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-fast ease-sfx group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-4 px-5 py-4" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="space-y-2">
          <span className="sfx-skeleton block h-3.5 w-4/5" />
          <span className="sfx-skeleton block h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}
